import React, { useContext, useMemo, useState } from "react";
import "../css/shop.scss";
import { useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import EmptyState from "./EmptyState";
import ProductGrid from "./ProductGrid";

const MATERIAL_FILTERS = [
  { value: "merino", label: "Merino wool" },
  { value: "wool", label: "Wool" },
  { value: "cotton", label: "Organic cotton" },
  { value: "linen", label: "Linen" },
  { value: "alpaca", label: "Alpaca" },
  { value: "silk", label: "Silk" },
  { value: "hemp", label: "Hemp" },
  { value: "cashmere", label: "Cashmere" },
];

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "new", label: "Newest" },
  { value: "price-asc", label: "Price, low to high" },
  { value: "price-desc", label: "Price, high to low" },
  { value: "name-asc", label: "Name, A–Z" },
];

const normalise = (value = "") =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const titleCase = (value = "") =>
  value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const truthyParam = (value) => ["1", "true", "yes"].includes(normalise(value));

const searchableProductText = (product) =>
  normalise(
    [
      product.name,
      product.category,
      product.collection,
      product.material,
      ...(product.tags || []),
      ...(Array.isArray(product.description) ? product.description : []),
      ...Object.values(product.attributes || {}),
    ].join(" ")
  );

const sortProducts = (products, sort) => {
  const sorted = [...products];

  switch (sort) {
    case "new":
      return sorted.sort(
        (left, right) =>
          Number(Boolean(right.isNew)) - Number(Boolean(left.isNew)) ||
          Number(right.id) - Number(left.id)
      );
    case "price-asc":
      return sorted.sort((left, right) => Number(left.price) - Number(right.price));
    case "price-desc":
      return sorted.sort((left, right) => Number(right.price) - Number(left.price));
    case "name-asc":
      return sorted.sort((left, right) => left.name.localeCompare(right.name));
    default:
      return sorted.sort(
        (left, right) =>
          Number(Boolean(right.isBestSeller)) -
          Number(Boolean(left.isBestSeller))
      );
  }
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { wishlistItems = [] } = useContext(CartContext);

  const collection = normalise(searchParams.get("collection"));
  const newOnly = normalise(searchParams.get("filter"))
    .split(/\s+/)
    .includes("new");
  const wishlistOnly = truthyParam(searchParams.get("wishlist"));
  const search = searchParams.get("search")?.trim() || "";
  const category = searchParams.get("category")?.trim() || "";
  const material = normalise(searchParams.get("material"));
  const sort = searchParams.get("sort") || "featured";

  const collections = useMemo(
    () =>
      Array.from(new Set(productData.map((product) => product.collection)))
        .filter(Boolean)
        .map((value) => ({ value, label: titleCase(value) })),
    []
  );

  const categories = useMemo(
    () =>
      Array.from(new Set(productData.map((product) => product.category)))
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right)),
    []
  );

  const visibleProducts = useMemo(() => {
    const wishlistIds = new Set(
      wishlistItems.map((item) => String(item?.id ?? item))
    );
    const searchTerm = normalise(search);
    const categoryTerm = normalise(category);

    const filtered = productData.filter((product) => {
      if (collection && normalise(product.collection) !== collection) return false;
      if (newOnly && !product.isNew) return false;
      if (wishlistOnly && !wishlistIds.has(String(product.id))) return false;
      if (categoryTerm && normalise(product.category) !== categoryTerm) return false;
      if (material && !normalise(product.material).includes(material)) return false;
      if (searchTerm && !searchableProductText(product).includes(searchTerm)) {
        return false;
      }

      return true;
    });

    return sortProducts(filtered, sort);
  }, [category, collection, material, newOnly, search, sort, wishlistItems, wishlistOnly]);

  const updateParam = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value === undefined || value === null || value === "" || value === false) {
      nextParams.delete(key);
    } else {
      nextParams.set(key, String(value));
    }

    setSearchParams(nextParams);
  };

  const clearFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    ["collection", "filter", "wishlist", "search", "category", "material"].forEach(
      (key) => nextParams.delete(key)
    );
    setSearchParams(nextParams);
    setFiltersOpen(false);
  };

  const activeFilters = [
    collection && {
      key: "collection",
      label: titleCase(collection),
      onRemove: () => updateParam("collection", ""),
    },
    newOnly && {
      key: "filter",
      label: "New arrivals",
      onRemove: () => updateParam("filter", ""),
    },
    wishlistOnly && {
      key: "wishlist",
      label: "Favourites",
      onRemove: () => updateParam("wishlist", ""),
    },
    search && {
      key: "search",
      label: `“${search}”`,
      onRemove: () => updateParam("search", ""),
    },
    category && {
      key: "category",
      label: category,
      onRemove: () => updateParam("category", ""),
    },
    material && {
      key: "material",
      label:
        MATERIAL_FILTERS.find((option) => option.value === material)?.label ||
        titleCase(material),
      onRemove: () => updateParam("material", ""),
    },
  ].filter(Boolean);

  const pageTitle = wishlistOnly
    ? "Favourites"
    : search
    ? `Results for “${search}”`
    : newOnly
    ? "New arrivals"
    : collection
    ? titleCase(collection)
    : "All pieces";

  const emptyMessage = wishlistOnly
    ? "Pieces you save will appear here, ready whenever you are."
    : "No pieces match this combination yet. Remove a filter to keep looking.";

  return (
    <main className="shop main">
      <header className="shop-header">
        <p className="shop-header__eyebrow">The collection</p>
        <h1 className="shop-header__title">{pageTitle}</h1>
        <p className="shop-header__description">
          Comfortable knitwear selected by our Hong Kong team, designed for daily
          wear and a long life.
        </p>
      </header>

      <div className="shop-toolbar" aria-label="Shop controls">
        <p className="shop-toolbar__count" aria-live="polite">
          {visibleProducts.length} {visibleProducts.length === 1 ? "piece" : "pieces"}
        </p>

        <div className="shop-toolbar__actions">
          <button
            className="shop-toolbar__filter-toggle"
            type="button"
            onClick={() => setFiltersOpen((isOpen) => !isOpen)}
            aria-expanded={filtersOpen}
            aria-controls="shop-filters"
          >
            Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ""}
            <span aria-hidden="true">{filtersOpen ? "−" : "+"}</span>
          </button>

          <label className="shop-toolbar__sort">
            <span>Sort by</span>
            <select
              value={SORT_OPTIONS.some((option) => option.value === sort) ? sort : "featured"}
              onChange={(event) => updateParam("sort", event.target.value)}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <aside
        className={`shop-filters${filtersOpen ? " shop-filters--open" : ""}`}
        id="shop-filters"
        aria-label="Product filters"
        hidden={!filtersOpen}
      >
        <div className="shop-filters__header">
          <h2 className="shop-filters__title">Filter the collection</h2>
          <button
            className="shop-filters__close"
            type="button"
            onClick={() => setFiltersOpen(false)}
            aria-label="Close filters"
          >
            ×
          </button>
        </div>

        <div className="shop-filters__body">
          <form
            className="shop-filters__search"
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              updateParam("search", formData.get("shop-search")?.toString().trim());
            }}
          >
            <label htmlFor="shop-search">Search pieces</label>
            <div className="shop-filters__search-row">
              <input
                id="shop-search"
                name="shop-search"
                type="search"
                key={search}
                defaultValue={search}
                placeholder="Name, fibre or style"
              />
              <button type="submit">Search</button>
            </div>
          </form>

          <fieldset className="shop-filters__group">
            <legend>Collection</legend>
            <button
              className={!collection ? "is-active" : ""}
              type="button"
              onClick={() => updateParam("collection", "")}
              aria-pressed={!collection}
            >
              All
            </button>
            {collections.map((option) => (
              <button
                className={collection === normalise(option.value) ? "is-active" : ""}
                type="button"
                key={option.value}
                onClick={() => updateParam("collection", option.value)}
                aria-pressed={collection === normalise(option.value)}
              >
                {option.label}
              </button>
            ))}
          </fieldset>

          <fieldset className="shop-filters__group">
            <legend>Category</legend>
            <button
              className={!category ? "is-active" : ""}
              type="button"
              onClick={() => updateParam("category", "")}
              aria-pressed={!category}
            >
              All
            </button>
            {categories.map((option) => (
              <button
                className={normalise(category) === normalise(option) ? "is-active" : ""}
                type="button"
                key={option}
                onClick={() => updateParam("category", option)}
                aria-pressed={normalise(category) === normalise(option)}
              >
                {option}
              </button>
            ))}
          </fieldset>

          <fieldset className="shop-filters__group">
            <legend>Material</legend>
            <button
              className={!material ? "is-active" : ""}
              type="button"
              onClick={() => updateParam("material", "")}
              aria-pressed={!material}
            >
              All
            </button>
            {MATERIAL_FILTERS.map((option) => (
              <button
                className={material === option.value ? "is-active" : ""}
                type="button"
                key={option.value}
                onClick={() => updateParam("material", option.value)}
                aria-pressed={material === option.value}
              >
                {option.label}
              </button>
            ))}
          </fieldset>

          <fieldset className="shop-filters__group shop-filters__group--availability">
            <legend>More</legend>
            <label>
              <input
                type="checkbox"
                checked={newOnly}
                onChange={(event) =>
                  updateParam("filter", event.target.checked ? "new" : "")
                }
              />
              New arrivals
            </label>
            <label>
              <input
                type="checkbox"
                checked={wishlistOnly}
                onChange={(event) =>
                  updateParam("wishlist", event.target.checked ? "true" : "")
                }
              />
              Favourites only
            </label>
          </fieldset>
        </div>

        <div className="shop-filters__footer">
          <button
            className="shop-filters__clear"
            type="button"
            onClick={clearFilters}
            disabled={activeFilters.length === 0}
          >
            Clear filters
          </button>
          <button
            className="shop-filters__apply"
            type="button"
            onClick={() => setFiltersOpen(false)}
          >
            Show {visibleProducts.length} {visibleProducts.length === 1 ? "piece" : "pieces"}
          </button>
        </div>
      </aside>

      {activeFilters.length > 0 && (
        <div className="shop-active-filters" aria-label="Active filters">
          {activeFilters.map((filter) => (
            <button
              className="shop-active-filters__item"
              type="button"
              key={filter.key}
              onClick={filter.onRemove}
              aria-label={`Remove ${filter.label} filter`}
            >
              {filter.label} <span aria-hidden="true">×</span>
            </button>
          ))}
          <button
            className="shop-active-filters__clear"
            type="button"
            onClick={clearFilters}
          >
            Clear all
          </button>
        </div>
      )}

      <section className="shop-results" aria-labelledby="shop-results-title">
        <h2 className="shop-results__title" id="shop-results-title">
          {pageTitle}
        </h2>
        {visibleProducts.length > 0 ? (
          <ProductGrid products={visibleProducts} label={`${pageTitle} products`} priorityCount={4} />
        ) : (
          <EmptyState
            title={wishlistOnly ? "Your favourites are waiting" : "No pieces found"}
            message={emptyMessage}
            actionLabel={activeFilters.length > 0 ? "Clear filters" : "View all pieces"}
            onAction={activeFilters.length > 0 ? clearFilters : undefined}
          />
        )}
      </section>
    </main>
  );
}

export default Shop;
